/**
 * Tic-Tac-Logic Game Moves
 *
 * Functions for player interactions with the puzzle.
 */

import {
  type CellValue,
  type GameState,
  type HintResult,
  EMPTY,
  X,
  O,
} from './types';
import { getIndex, getCell } from './grid';
import { updateErrorFlags, checkComplete, isImmutable } from './validation';
import { getHint as solverGetHint } from './solver';

/**
 * Fill a cell with a value (X or O)
 */
export function fillCell(state: GameState, x: number, y: number, value: CellValue): boolean {
  if (state.completed) return false;
  if (isImmutable(state, x, y)) return false;

  const idx = getIndex(state.size, x, y);
  state.cells[idx] = value;

  updateErrorFlags(state);
  checkComplete(state);

  return true;
}

/**
 * Clear a cell
 */
export function clearCell(state: GameState, x: number, y: number): boolean {
  if (state.completed) return false;
  if (isImmutable(state, x, y)) return false;

  const idx = getIndex(state.size, x, y);
  state.cells[idx] = EMPTY;

  updateErrorFlags(state);

  return true;
}

/**
 * Cycle cell value (empty -> X -> O -> empty)
 */
export function cycleCell(state: GameState, x: number, y: number): boolean {
  if (state.completed) return false;
  if (isImmutable(state, x, y)) return false;

  const idx = getIndex(state.size, x, y);
  const current = state.cells[idx] ?? EMPTY;

  let next: CellValue;
  if (current === EMPTY) {
    next = X;
  } else if (current === X) {
    next = O;
  } else {
    next = EMPTY;
  }

  state.cells[idx] = next;

  updateErrorFlags(state);
  checkComplete(state);

  return true;
}

/**
 * Move selection
 */
export function moveSelection(state: GameState, dx: number, dy: number): void {
  state.selectedX = Math.max(0, Math.min(state.size - 1, state.selectedX + dx));
  state.selectedY = Math.max(0, Math.min(state.size - 1, state.selectedY + dy));
}

/**
 * Set selection to specific cell
 */
export function setSelection(state: GameState, x: number, y: number): void {
  state.selectedX = Math.max(0, Math.min(state.size - 1, x));
  state.selectedY = Math.max(0, Math.min(state.size - 1, y));
}

/**
 * Get hint for current state
 */
export function requestHint(state: GameState): HintResult | null {
  if (state.completed) return null;

  const hint = solverGetHint(state.cells, state.solution, state.size);

  if (hint) {
    // Fill the hint cell
    const idx = getIndex(state.size, hint.x, hint.y);
    state.cells[idx] = hint.value;
    state.cheated = true;

    updateErrorFlags(state);
    checkComplete(state);

    return hint;
  }

  return null;
}

/**
 * Get current cell value
 */
export function getCellValue(state: GameState, x: number, y: number): CellValue {
  return getCell(state.cells, state.size, x, y);
}

/**
 * Check if cell is selected
 */
export function isSelected(state: GameState, x: number, y: number): boolean {
  return state.selectedX === x && state.selectedY === y;
}

/**
 * Get counts for row (for UI display)
 */
export function getRowCounts(state: GameState, row: number): { x: number; o: number; target: number } {
  let xCount = 0;
  let oCount = 0;

  for (let x = 0; x < state.size; x++) {
    const value = getCell(state.cells, state.size, x, row);
    if (value === X) xCount++;
    else if (value === O) oCount++;
  }

  return { x: xCount, o: oCount, target: state.size / 2 };
}

/**
 * Get counts for column (for UI display)
 */
export function getColumnCounts(state: GameState, col: number): { x: number; o: number; target: number } {
  let xCount = 0;
  let oCount = 0;

  for (let y = 0; y < state.size; y++) {
    const value = getCell(state.cells, state.size, col, y);
    if (value === X) xCount++;
    else if (value === O) oCount++;
  }

  return { x: xCount, o: oCount, target: state.size / 2 };
}
